/**
 * Cart drawer — auto-gift controller (adapted for Supreme theme).
 * Adds/removes free gift line items when spend crosses tier thresholds.
 */
(function () {
  'use strict';

  var routes = window.themeRoutes || {
    cart_add_url: '/cart/add',
    cart_change_url: '/cart/change',
    cart_url: '/cart'
  };

  var _inFlight = false;
  var _pending = false;
  var _cachedConfigs = null;
  var _prevGiftCounts = new Map();
  var _rejectedTiers = new Set();

  function rejectionKey(blockId, tierIndex) {
    return 'autoGiftRejected:' + blockId + ':' + tierIndex;
  }

  function countKey(blockId, tierIndex) {
    return String(blockId) + ':' + String(tierIndex);
  }

  function isRejected(blockId, tierIndex) {
    return _rejectedTiers.has(rejectionKey(blockId, tierIndex));
  }

  function setRejected(blockId, tierIndex, val) {
    var key = rejectionKey(blockId, tierIndex);
    if (val) _rejectedTiers.add(key);
    else _rejectedTiers.delete(key);
  }

  function readConfigs() {
    if (_cachedConfigs && _cachedConfigs.length) return _cachedConfigs;
    var list = document.querySelectorAll('cart-drawer-auto-gift script[data-auto-gift-config]');
    var out = [];
    list.forEach(function (el) {
      try {
        var cfg = JSON.parse(el.textContent || '{}');
        if (cfg && cfg.blockId && Array.isArray(cfg.tiers) && cfg.tiers.length) out.push(cfg);
      } catch (error) { /* ignore malformed config */ }
    });
    if (out.length) _cachedConfigs = out;
    return out;
  }

  function sectionsParam() {
    var sections = window.themeCartSections || ['cart-drawer'];
    return {
      sections: sections.join(','),
      sections_url: window.location.pathname || '/'
    };
  }

  function fetchCart() {
    return fetch(routes.cart_url + '.js', { cache: 'no-store', credentials: 'same-origin' })
      .then(function (response) { return response.ok ? response.json() : null; })
      .catch(function () { return null; });
  }

  function qualifyingSpendCents(cart) {
    if (typeof window.qualifyingSpendCentsFromCart === 'function') {
      return window.qualifyingSpendCentsFromCart(cart);
    }
    if (!Array.isArray(cart.items)) return 0;
    var total = 0;
    cart.items.forEach(function (item) {
      var props = (item && item.properties) || {};
      if (props._drawer_gift_selector) return;
      var price = typeof item.original_line_price === 'number' ? item.original_line_price :
        typeof item.final_line_price === 'number' ? item.final_line_price : 0;
      total += price;
    });
    return total;
  }

  function existingGiftsForTier(cart, blockId, tierIndex) {
    if (!Array.isArray(cart.items)) return [];
    return cart.items.filter(function (item) {
      var props = (item && item.properties) || {};
      if (String(props._drawer_gift_selector) !== String(blockId)) return false;
      return String(props._drawer_gift_tier_index || '1') === String(tierIndex);
    });
  }

  /* Sizes present in the cart (gift lines excluded), most common first,
     weighted by quantity. Used to match the gift's size to what the
     customer is actually buying. */
  function preferredSizes(cart) {
    var counts = {};
    var order = [];
    (cart.items || []).forEach(function (item) {
      var props = (item && item.properties) || {};
      if (props._drawer_gift_selector) return;
      (item.options_with_values || []).forEach(function (option) {
        if (!option || !/size/i.test(option.name || '')) return;
        var value = String(option.value || '').trim().toLowerCase();
        if (!value) return;
        if (!(value in counts)) {
          counts[value] = 0;
          order.push(value);
        }
        counts[value] += item.quantity || 1;
      });
    });
    order.sort(function (a, b) { return counts[b] - counts[a]; });
    return order;
  }

  /* Pick the variant of one gift product that best matches the cart's
     sizes; falls back to the first available variant. */
  function pickVariant(entry, sizes) {
    var variants = (entry && entry.v) || [];
    if (!variants.length) return null;
    for (var i = 0; i < sizes.length; i += 1) {
      for (var j = 0; j < variants.length; j += 1) {
        if (String(variants[j].s || '').trim().toLowerCase() === sizes[i]) {
          return variants[j];
        }
      }
    }
    return variants[0];
  }

  function tierEntries(tier) {
    if (Array.isArray(tier.p)) return tier.p;
    /* Legacy flat variant-id config */
    if (Array.isArray(tier.v)) {
      return tier.v.map(function (id) {
        return { pid: null, v: [{ id: id, s: '' }] };
      });
    }
    return [];
  }

  function shuffled(arr) {
    var copy = arr.slice();
    for (var i = copy.length - 1; i > 0; i -= 1) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = copy[i];
      copy[i] = copy[j];
      copy[j] = tmp;
    }
    return copy;
  }

  function cartRequest(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) throw data;
        return data;
      });
    });
  }

  function postAdd(blockId, tierIndex, variantPool) {
    var sp = sectionsParam();
    var pool = variantPool.slice();
    var attempt = function () {
      if (!pool.length) return Promise.resolve(null);
      var variantId = pool.shift();
      return cartRequest(routes.cart_add_url + '.js', {
        items: [{
          id: variantId,
          quantity: 1,
          properties: {
            _drawer_gift_selector: String(blockId),
            _drawer_gift_tier_index: String(tierIndex),
            _drawer_auto_gift: 'true'
          }
        }],
        sections: sp.sections,
        sections_url: sp.sections_url
      }).catch(function () {
        return attempt();
      });
    };
    return attempt();
  }

  function postRemove(lineKey) {
    var sp = sectionsParam();
    return cartRequest(routes.cart_change_url + '.js', {
      id: lineKey,
      quantity: 0,
      sections: sp.sections,
      sections_url: sp.sections_url
    }).catch(function () { return null; });
  }

  function paint(response) {
    if (!response || !response.sections) return;
    if (typeof window.themeCartRefreshSections === 'function') {
      window.themeCartRefreshSections(response.sections);
    }
  }

  function reconcileOnce() {
    var configs = readConfigs();
    if (!configs.length) return Promise.resolve(null);

    return fetchCart().then(function processCart(cart) {
      if (!cart || !Array.isArray(cart.items)) return null;

      if (cart.items.length === 0) {
        configs.forEach(function (cfg) {
          cfg.tiers.forEach(function (tier) {
            setRejected(cfg.blockId, tier.i, false);
            _prevGiftCounts.set(countKey(cfg.blockId, tier.i), 0);
          });
        });
        return null;
      }

      var blockIdx = 0;
      var tierIdx = 0;
      var lastResp = null;

      function nextTier() {
        if (blockIdx >= configs.length) return Promise.resolve(lastResp);

        var cfg = configs[blockIdx];
        if (tierIdx >= cfg.tiers.length) {
          blockIdx += 1;
          tierIdx = 0;
          return nextTier();
        }

        var tier = cfg.tiers[tierIdx];
        tierIdx += 1;
        var blockId = cfg.blockId;
        var threshold = Number(tier.t) || 0;
        var entries = tierEntries(tier);
        if (tier.s === 'collection') entries = shuffled(entries);
        var sizes = preferredSizes(cart);
        var pool = entries
          .map(function (entry) { return pickVariant(entry, sizes); })
          .filter(Boolean)
          .map(function (variant) { return variant.id; });
        var existing = existingGiftsForTier(cart, blockId, tier.i);
        var ck = countKey(blockId, tier.i);
        var prevCount = _prevGiftCounts.get(ck) || 0;
        var spend = qualifyingSpendCents(cart);

        if (spend >= threshold && prevCount > 0 && existing.length === 0) {
          setRejected(blockId, tier.i, true);
        }
        _prevGiftCounts.set(ck, existing.length);

        var action = Promise.resolve(null);

        if (spend >= threshold) {
          if (existing.length === 0 && pool.length > 0 && !isRejected(blockId, tier.i)) {
            action = postAdd(blockId, tier.i, pool);
          } else if (existing.length === 1) {
            /* Size sync: if the cart's dominant size changed and the same
               gift product has a size-matched variant, swap the gift over.
               Add first, remove after, so a failed add can't lose the gift. */
            var line = existing[0];
            var entry = null;
            for (var e = 0; e < entries.length; e += 1) {
              if (String(entries[e].pid) === String(line.product_id)) {
                entry = entries[e];
                break;
              }
            }
            if (entry && sizes.length) {
              var desired = pickVariant(entry, sizes);
              var desiredMatchesCart = desired &&
                sizes.indexOf(String(desired.s || '').trim().toLowerCase()) > -1;
              if (desiredMatchesCart && String(desired.id) !== String(line.id)) {
                action = postAdd(blockId, tier.i, [desired.id]).then(function (response) {
                  if (!response) return null;
                  return postRemove(line.key).then(function (removeResponse) {
                    return removeResponse || response;
                  });
                });
              }
            }
          } else if (existing.length > 1) {
            action = postRemove(existing[existing.length - 1].key);
          }
        } else {
          setRejected(blockId, tier.i, false);
          if (existing.length > 0) {
            action = postRemove(existing[0].key);
          }
        }

        return action.then(function (response) {
          if (response) {
            lastResp = response;
            return fetchCart().then(function (freshCart) {
              cart = freshCart || cart;
              return nextTier();
            });
          }
          return nextTier();
        });
      }

      return nextTier();
    });
  }

  function drain() {
    if (_inFlight) {
      _pending = true;
      return;
    }
    _inFlight = true;

    function loop() {
      return reconcileOnce().then(function (response) {
        if (response) paint(response);
        if (_pending) {
          _pending = false;
          return loop();
        }
      });
    }

    loop().finally(function () {
      _inFlight = false;
    });
  }

  window.__autoGiftRejectTier = function (blockId, tierIndex) {
    if (blockId == null || tierIndex == null) return;
    setRejected(String(blockId), String(tierIndex), true);
  };

  document.addEventListener('cart:updated', drain);

  /* Safety net: reconcile whenever the cart drawer is opened, covering carts
     mutated outside this page (another tab, checkout back-navigation). */
  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-drawer-open="CartDrawer"]')) drain();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', drain, { once: true });
  } else {
    drain();
  }
})();
